import { baseApi } from "./baseApi";

export interface ExpenseRecord {
  id: number;
  name: string;
  purpose: string;
  date: string;       // ISO date string: "YYYY-MM-DD"
  amount: number;
  createdAt?: string;
}

interface ExpenseApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors: string[];
}

export interface CreateExpensePayload {
  name: string;
  purpose: string;
  date: string;
  amount: number;
}

export interface UpdateExpensePayload extends CreateExpensePayload {
  id: number;
}

const unwrapData = <T>(res: ExpenseApiResponse<T>): T => res.data;

const normalizeExpense = (raw: Record<string, unknown>): ExpenseRecord => ({
  id: Number(raw.id ?? raw.expenseId ?? 0),
  name: String(raw.name ?? raw.expenseName ?? ""),
  purpose: String(raw.purpose ?? raw.description ?? ""),
  date: String(raw.date ?? raw.expenseDate ?? ""),
  amount: Number(raw.amount ?? raw.totalExpense ?? raw.total ?? 0),
  createdAt: raw.createdAt ? String(raw.createdAt) : undefined,
});

const expensesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    fetchExpenses: build.query<ExpenseRecord[], void>({
      query: () => "/Expense",
      transformResponse: (res: ExpenseApiResponse<Record<string, unknown>[]>) =>
        (unwrapData(res) ?? []).map(normalizeExpense),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Expense" as const, id })),
              { type: "Expense", id: "LIST" },
            ]
          : [{ type: "Expense", id: "LIST" }],
    }),

    createExpense: build.mutation<ExpenseRecord, CreateExpensePayload>({
      query: (body) => ({ url: "/Expense", method: "POST", body }),
      transformResponse: (res: ExpenseApiResponse<Record<string, unknown>>) =>
        normalizeExpense(unwrapData(res)),
      invalidatesTags: [{ type: "Expense", id: "LIST" }],
    }),

    updateExpense: build.mutation<ExpenseRecord, UpdateExpensePayload>({
      query: ({ id, ...body }) => ({
        url: `/Expense/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: (res: ExpenseApiResponse<Record<string, unknown>>) =>
        normalizeExpense(unwrapData(res)),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Expense", id },
        { type: "Expense", id: "LIST" },
      ],
    }),

    deleteExpense: build.mutation<void, number>({
      query: (id) => ({ url: `/Expense/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Expense", id },
        { type: "Expense", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useFetchExpensesQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
} = expensesApi;
